import { Link } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { ATTENDANCE_LABELS, type AttendanceStatus, type SeasonAttendance as Data } from '@hoopify/shared';
import { api, errorMessage } from '../../lib/api';
import { formatShortDay } from '../../lib/dates';
import { Alert, LoadingBlock } from '../ui';

const SYMBOL: Record<AttendanceStatus, { s: string; cls: string }> = {
  present: { s: '✓', cls: 'text-ok' },
  late: { s: 'L', cls: 'text-warn' },
  sitting_out: { s: 'S', cls: 'text-warn' },
  absent: { s: '✗', cls: 'text-danger' },
};

/** Who came to what, across the season. */
export function SeasonAttendance({ teamId, seasonId }: { teamId: string; seasonId: string }) {
  const q = useQuery({ queryKey: ['season-attendance', seasonId], queryFn: () => api.get<Data>(`/teams/${teamId}/seasons/${seasonId}/attendance`) });
  if (q.isLoading) return <LoadingBlock />;
  if (q.isError) return <Alert>{errorMessage(q.error)}</Alert>;
  const d = q.data!;
  if (!d.sessions.length) return <p className="text-muted">Once you take attendance at sessions, each player’s record shows here.</p>;
  return (
    <div>
      <div className="overflow-x-auto rounded-lg border border-line bg-surface">
        <table className="tnum w-full text-[14px]">
          <thead>
            <tr className="border-b border-line text-left text-[12px] text-muted">
              <th className="sticky left-0 bg-surface px-3 py-2 font-medium">Player</th>
              <th className="px-3 py-2 text-right font-medium">Rate</th>
              {d.sessions.map((s) => (
                <th key={s.id} className="px-1.5 py-2 text-center font-medium whitespace-nowrap">
                  <Link to={`/teams/${teamId}/sessions/${s.id}`} title={s.title} className="hover:text-ink">
                    {formatShortDay(s.date).replace(/^\w+ /, '')}
                  </Link>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {d.players.map((p) => (
              <tr key={p.id} className="border-b border-line last:border-0">
                <td className="sticky left-0 bg-surface px-3 py-1.5 whitespace-nowrap">
                  {p.number && <span className="mr-1.5 text-muted">{p.number}</span>}
                  {p.name}
                </td>
                <td className={`px-3 py-1.5 text-right font-medium ${p.rate !== null && p.rate < 0.6 ? 'text-warn' : ''}`}>{p.rate === null ? '' : `${Math.round(p.rate * 100)}%`}</td>
                {d.sessions.map((s) => {
                  const m = p.marks[s.id];
                  return (
                    <td key={s.id} className={`px-1.5 py-1.5 text-center font-semibold ${m ? SYMBOL[m].cls : 'text-faint'}`} title={m ? ATTENDANCE_LABELS[m] : 'Not marked'}>
                      {m ? SYMBOL[m].s : '·'}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="mt-2 text-[13px] text-muted">✓ here, L late, S sitting out, ✗ absent. Late and sitting out count as attending.</p>
    </div>
  );
}
